import React, { useState, useRef, useEffect } from "react";
import { IconButton, TextField, Box, Paper, Typography } from "@mui/material";
import { Message, Close, Send } from "@mui/icons-material";
import LoaderNew from "../Loader/LoaderNew";

export default function ChatWindow() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { sender: "bot", text: "Hello! How can I assist you?" },
  ]);
  const [input, setInput] = useState("");
  const [loader, setLoader] = useState(false);
  const [puterReady, setPuterReady] = useState(false);

  const messagesEndRef = useRef(null);

  /* ================================
     Load Puter.js safely (ONCE)
  ================================= */
  useEffect(() => {
    // Already loaded
    if (window.puter) {
      setPuterReady(true);
      return;
    }

    // Avoid loading script multiple times
    const existingScript = document.querySelector(
      'script[src="https://js.puter.com/v2/"]'
    );
    if (existingScript) {
      existingScript.addEventListener("load", () => setPuterReady(true));
      return;
    }

    const script = document.createElement("script");
    script.src = "https://js.puter.com/v2/";
    script.async = true;

    script.onload = () => {
      setPuterReady(true);
    };

    script.onerror = () => {
      // ❌ Screen ya console error show nahi karega
      // silently fail
      setPuterReady(false);
    };

    document.body.appendChild(script);

    return () => {
      script.onload = null;
      script.onerror = null;
    };
  }, []);

  /* ================================
     Auto scroll on new message
  ================================= */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /* ================================
     Send Message (Puter AI)
  ================================= */
  const handleSendMessage = async () => {
    // ⛔ AI script not ready
    if (!puterReady) {
      alert("AI is still loading, please wait...");
      return;
    }

    // ⛔ Empty message
    if (!input.trim()) return;

    const userMessage = input;

    // Show user message instantly
    setMessages((prev) => [...prev, { sender: "user", text: userMessage }]);
    setInput("");

    try {
      setLoader(true);

      // Safety check
      if (!window.puter || !window.puter.ai) {
        throw new Error("Puter AI not available");
      }

      // 🔥 Call Puter AI
      const response = await window.puter.ai.chat(userMessage, {
        model: "google/gemini-2.5-pro",
      });

      // 🔥 Extract ONLY text (important)
      const aiText =
        typeof response === "string"
          ? response
          : response?.message?.content ||
            response?.result?.message?.content ||
            "No response from AI";

      // Show AI message
      setMessages((prev) => [...prev, { sender: "bot", text: aiText }]);
    } catch (error) {
      console.error("AI Error:", error);
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "Something went wrong ❌" },
      ]);
    } finally {
      setLoader(false);
    }
  };

  return (
    <Box position="fixed" bottom={20} right={20} zIndex={1000}>
      {isOpen ? (
        <Paper
          elevation={6}
          sx={{
            width: { xs: "90vw", sm: 400 },
            height: { xs: "70vh", sm: 400 },
            p: 2,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "white",
          }}
        >
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom={1}
            pb={1}
          >
            <Typography fontWeight="bold">General AI Chat</Typography>
            <IconButton onClick={() => setIsOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box
            flex={1}
            p={1}
            sx={{
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {loader && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <LoaderNew size={30} />
              </Box>
            )}

            {!loader &&
              messages.map((msg, index) => (
                <Typography
                  key={index}
                  align={msg.sender === "user" ? "right" : "left"}
                  sx={{
                    backgroundColor:
                      msg.sender === "user" ? "primary.light" : "grey.200",
                    borderRadius: 2,
                    px: 1,
                    py: 0.5,
                    maxWidth: "75%",
                    alignSelf:
                      msg.sender === "user" ? "flex-end" : "flex-start",
                  }}
                >
                  {msg.text}
                </Typography>
              ))}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <Box display="flex" borderTop={1} pt={1}>
            <TextField
              fullWidth
              placeholder={puterReady ? "Ask anything..." : "Loading AI..."}
              size="small"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
              disabled={!puterReady || loader}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={!puterReady || loader}
            >
              <Send />
            </IconButton>
          </Box>
        </Paper>
      ) : (
        /* Floating Button */
        <IconButton
          onClick={() => setIsOpen(true)}
          color="primary"
          sx={{
            position: "fixed",
            bottom: { xs: 10, sm: 20 },
            right: { xs: 10, sm: 20 },
            backgroundColor: "primary.main",
            color: "white",
            boxShadow: 3,
            "&:hover": {
              backgroundColor: "white",
              color: "primary.main",
            },
          }}
        >
          <Message />
        </IconButton>
      )}
    </Box>
  );
}
